import { useEffect, useRef, useState } from 'react';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Text } from '../../components/Text';
import { PasswordField } from '../../components/PasswordField';
import { TextField } from '../../components/TextField';
import { loginTexts, errorMessages, awsExports ,colors} from '../../constants/constants';
import { ErrorObjectLoginSchema, ErrorObjectSchema, RequiredObjectType } from './login.type';

// Redux state and dispatch hook for accessing and updating the Redux store.
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import {
    changeAccessTokenValue,
    changeTestTakerUsernameValue,
    changeTestTaker,
    changeTestTakerSessionIdValue,
    changeInsperaDetails,
    resetStore,
    changeCalibrationDetails,
} from '../../redux/appConfigSlice';

import { useNavigate } from 'react-router-dom';
import { signIn, signOut, getCurrentUser, fetchAuthSession, GetCurrentUserOutput, AuthSession } from '@aws-amplify/auth';
import classes from './LoginScreen.module.css';

// Configure Amplify in index file or root file
// import { Amplify } from "@aws-amplify/core";
import { Amplify } from 'aws-amplify';
import {
    fetchHealthCheck,
    fetchIntentHealthCheck,
    fetchTestTakerSessionId,
    sendMessageToInsperaIB,
    validateInsperaAccessToken,
} from '../../api/authApis';
import { ErrorCard } from '../../components/ErrorCard';
import errorCodeDescription from '../../components/ErrorCard/errorProperties';
import { InsperaObject } from '../../interface/interface';
Amplify.configure(awsExports);

/**
 * Check if application is opned in iFrame or not
 * @returns true if the application is opened in iFrame or in any other object or container else return false
 */
export const isAppOpenedInIframe = () => {
    return window.self !== window.top;
};

// Login screen component that displays the Login of the selected options.
const LoginScreen = () => {
    /* ********************************** Inspera integration code START ********************************** */
    /**
     * Check if application is opened in iFrame or in browser
     * if it is opened in iFrame then take access_token and username and required details from postMessage method
     * and pass this access_token to the backend to validate and get new updated access_token
     */
    let inSperaLogin = useRef<any>(false);
    let externalValueForUserName = useRef<any>('');
    const [colour, setColour] = useState<string>(colors.ielts.primaryColor);

    useEffect(() => {
        dispatch(resetStore());
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty('--primary-red', colour);
        document.documentElement.style.setProperty('--semantic-bright-red', colour);
        
    }, [colour]);

    const validateInsperaToken = async (props: any) => {
        const { token } = props;
        const resData = await validateInsperaAccessToken({ accessToken: token });

        /**
         * validateToken API integration after getting the token from Inspera browser
         */

        const urlParams = new URLSearchParams(window.location.search);
        const userID = urlParams.get('UserID');
        const assessmentID = urlParams.get('AssessmentID');
        const externalId = urlParams.get('ExternalID');
        const partner = urlParams.get('Partner');
        const product = urlParams.get('Product');
        if (!resData?.error) {
            if (resData?.data?.tokenVerified) {
              
                externalValueForUserName.current = externalId;
                dispatch(changeTestTaker({ testTakerId: userID }));

                handleSubmit('', {
                    assessmentId: assessmentID ?? '',
                    externalId: externalId ?? '',
                    partner: partner ?? '',
                    product: product ?? 'GT'
                });
            } else {
                sendMessageToInsperaIB({
                    type: 'AUTH_FAILED',
                    ...resData,
                });
            }
        } else {
            console.log('ERROR in validating the token: ', resData?.errorMessage);
            setErrorCode( resData?.errorCode);
            console.error(resData?.message);
            sendMessageToInsperaIB({
                type: 'AUTH_FAILED',
                ...resData,
            });
        }
    };

    /**
     * Check if application is opened in iframe or in browser
     * and validate the token got from Inspera
     */
    useEffect(() => {
        if (isAppOpenedInIframe()) {
            sendMessageToInsperaIB({
                type: 'READY',
            });
            inSperaLogin.current = true;
        }

        const handleMessage = (event: any) => {
            console.log('Message received from ICMST Inspera POC:', event.data);
            const eventData = event?.data;
            if (isAppOpenedInIframe() && eventData.type === 'AUTH_TOKEN') {
                validateInsperaToken(eventData);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    /* ********************************** Inspera integration code END ********************************** */

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [reqObject, setReqObject] = useState<RequiredObjectType>({
        email: '',
        password: '',
    });
    const schemaErrorObj: ErrorObjectSchema = {
        emailError: false,
        emailErrorText: '',
        passwordError: false,
        passwordErrorText: '',
    };
    const schemaLoginErrorObj: ErrorObjectLoginSchema = {
        loginError: false,
        loginErrorMessage: '',
    };
    const [errorObj, setErrorObj] = useState<ErrorObjectSchema>(schemaErrorObj);
    const [loginErrorObj, setLoginErrorObj] = useState<ErrorObjectLoginSchema>(schemaLoginErrorObj);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showLogin, setShowLogin] = useState<boolean>(true);
    let apiUpStatusRef = useRef<boolean | null>(null);
    const {
        testTakerId,
        // silenceMeanValue
    } = useSelector((state: RootState) => state.appConfigSlice.testTaker);
    const [isSessionId, setIsSessionId] = useState(false);
    const [errorCode, setErrorCode] = useState<string>('');
    const appTheme = useSelector((state: RootState) => state.appConfigSlice.appTheme);
    /**
     * Fetch the current user session id
     * @param testTakerAccessToken
     * @param username
     */
    const getTestTakerSessionId = async (testTakerAccessToken: string | undefined, username: string, testTakerId: any) => {
        try {
            let userNameForLogin;
            if (inSperaLogin.current) {
                userNameForLogin = externalValueForUserName.current;
            } else {
                userNameForLogin = username;
            }
            // Get the session info from the API for the given testTakerId.
            const uniqueSessionInfo = await fetchTestTakerSessionId(testTakerAccessToken, userNameForLogin);
            if (typeof uniqueSessionInfo === 'undefined' || uniqueSessionInfo.status !== 200) {
                const apiErrorCode = uniqueSessionInfo?.errorCode;
                setErrorCode(apiErrorCode||'');
                console.error(uniqueSessionInfo?.message);
            }

            let intentHealthCheck;
            await fetchIntentHealthCheck(encodeURIComponent(uniqueSessionInfo.data), testTakerAccessToken)
            .then((res: any) => {
                    if (!res.service) {
                        intentHealthCheck = false;
                        return;
                    }
                    intentHealthCheck = true;
                })
                .catch((error:any) => {
                    intentHealthCheck = false;
                    const apiErrorCode = error?.response?.errorCode || error?.errorCode ;
                    setErrorCode(apiErrorCode||'');
                    console.error(error?.response?.message);
                });
            if (uniqueSessionInfo && apiUpStatusRef.current && intentHealthCheck) {
                dispatch(changeTestTakerSessionIdValue(uniqueSessionInfo?.data));
                navigate('/precheck');
            } else {
                console.error(`Unable to generate uniqueSessionId for user ${username},testTakerID: ${testTakerId}`);
                setIsSessionId(true);
            }
        } catch (error: any) {
            console.log('Error in fetching sessionId: ', error);
            const apiErrorCode = error?.uniqueSessionInfo?.errorCode;
            setErrorCode(apiErrorCode || '');
            //Display the session id error message
            setLoginErrorObj({
                loginError: true,
                loginErrorMessage: `Test session couldn’t be started for ${username}. Please contact support.` || error,
            });
        }
    };

    // form validation
    const formValidation = () => {
        let errorFlag = false;
        if (reqObject.email === '' && reqObject.password === '') {
            setErrorObj({
                emailError: true,
                emailErrorText: errorMessages.EMPTY_USERNAME,
                passwordError: true,
                passwordErrorText: errorMessages.EMPTY_PASSWORD,
            });
            errorFlag = true;
        } else if (reqObject.email === '') {
            setErrorObj({
                ...errorObj,
                emailError: true,
                emailErrorText: errorMessages.EMPTY_USERNAME,
                passwordError: false,
                passwordErrorText: '',
            });
            errorFlag = true;
        } else if (reqObject.password === '') {
            setErrorObj({
                ...errorObj,
                emailError: false,
                emailErrorText: '',
                passwordError: true,
                passwordErrorText: errorMessages.EMPTY_PASSWORD,
            });
            errorFlag = true;
        } else {
            setErrorObj({
                emailError: false,
                emailErrorText: '',
                passwordError: false,
                passwordErrorText: '',
            });
        }
        return errorFlag;
    };

    const setUserDetails = async (props: any) => {
        const { testTakerAccessToken, testTakerUserName, testTakerId } = props;
        // Save the access token, testTaker username and testTaker id in Redux storage
        dispatch(changeAccessTokenValue(testTakerAccessToken));
        dispatch(changeTestTakerUsernameValue(testTakerUserName));
        await fetchHealthCheck()
            .then((response) => {
                if (response && !response.atc && !response.service) {
                    apiUpStatusRef.current = false;
                    return;
                }
                apiUpStatusRef.current = true;
            })
            .catch(() => {
                apiUpStatusRef.current = false;
            });

        if (!isAppOpenedInIframe()) {
            dispatch(changeTestTaker({ testTakerId: testTakerId }));
        } // Set testTaker sessionId
        /**
         * @params accessToken and username value to to set the unique sessionId
         * username of testing-user@cambridgeTesting would be testing-user only
         */
        let tempSplitUsername = testTakerUserName?.split('@')[0];
        getTestTakerSessionId(testTakerAccessToken, tempSplitUsername ?? '', testTakerId);
    };

    // Method to handle the successful login and save the username, userId and token information in redux storage
    const handleLoginSuccess = async (inspera?: InsperaObject) => {
        setShowLogin(false);
        let userDetails: GetCurrentUserOutput = await getCurrentUser();
        console.log('userdetails {} ', userDetails);
        let session: AuthSession = await fetchAuthSession();
        let testTakerAccessToken = session?.tokens?.accessToken.toString();
        let group: string | undefined;
        //group = session?.tokens?.idToken?.payload["cognito:groups"]?.[0] || [];

        const groups = session?.tokens?.idToken?.payload['cognito:groups'];

        console.log(groups);
        if (Array.isArray(groups)) {
            group = groups[0] as string;
        }

        console.log('testTakerAccessToken groups {} ', group);
        let product = group;

        if (group?.includes('HOELT')) {
            const splittedgroup = group.split('_');
            product = splittedgroup[0];
            console.log(splittedgroup, 'groups');
            dispatch(
                changeCalibrationDetails({
                    status: splittedgroup[2],
                    level: splittedgroup[1],
                })
            );
        }

        dispatch(
            changeInsperaDetails({
                assessmentId: inspera?.assessmentId ?? '',
                externalId: inspera?.externalId ?? '',
                partner: inspera?.partner ?? '',
                product: product ?? 'GT',
            })
        );
        let tempUserName = userDetails.signInDetails?.loginId;
        let testTakerId = userDetails.userId;
        if (isAppOpenedInIframe()) {
            sendMessageToInsperaIB({
                type: 'AUTH_SUCCESS',
            });

            return setUserDetails({
                testTakerAccessToken: testTakerAccessToken,
                testTakerUserName: tempUserName,
            });
        }

        setUserDetails({
            testTakerAccessToken: testTakerAccessToken,
            testTakerUserName: tempUserName,
            testTakerId: testTakerId,
        });

        if(product === 'HOELT'){
            setColour(colors.hoelt.primaryColor);
        }

    };

    // method to call on submit
    const handleSubmit = async (e?: any, inspera?: InsperaObject) => {
        await signOut();
        const loginObj = {
            username: reqObject.email,
            password: reqObject.password,
        };
        setLoginErrorObj({ loginError: false, loginErrorMessage: '' });
        // signout if loggedin already
        if (isAppOpenedInIframe()) {
            loginObj.username = process.env.REACT_APP_INSPERA_USERNAME ?? '';
            loginObj.password = process.env.REACT_APP_INSPERA_PASSWORD ?? '';

            loginFn(loginObj, inspera);
        }

        if (!formValidation()) {
            setIsLoading(true);
            loginFn(loginObj);
        }
       
    };

    const loginFn = (loginObj: { username: string; password: string }, inspera?: InsperaObject) => {
        signIn(loginObj).then(
            (loginResponse: any) => {
                setIsLoading(false);
                if (loginResponse.isSignedIn) {
                    handleLoginSuccess(inspera);
                } else {
                    setLoginErrorObj({
                        loginError: true,
                        loginErrorMessage: loginResponse.nextStep?.signInStep,
                    });
                }
            },
            (error: any) => {
                setIsLoading(false);
                setLoginErrorObj({
                    loginError: true,
                    loginErrorMessage: error.message,
                });
                console.log('Sign in error: ', error);
            }
        );
    };

    // key down event triggered from email and password fields
    const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        // if enter is pressed, calling handleSubmit method
        if (event.key === loginTexts.ENTER_TEXT) {
            if (showLogin) {
                handleSubmit();
            }
        }
    };

    const onChangeUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
        setErrorObj((prevState) =>
            !e.target.value
                ? { ...errorObj, emailError: true, emailErrorText: errorMessages.EMPTY_USERNAME }
                : { ...prevState, emailError: false }
        );
        setReqObject((prevState) => ({ ...prevState, email: e.target.value }));
    };

    const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setErrorObj((prevState) =>
            !e.target.value
                ? { ...errorObj, passwordError: true, passwordErrorText: errorMessages.EMPTY_PASSWORD }
                : { ...prevState, passwordError: false }
        );
        setReqObject((prevState) => ({ ...prevState, password: e.target.value }));
    };

    return !errorCode ? (
        <Card className={classes.formContainer}>
            {isAppOpenedInIframe() && (
                <div className={classes.form}>
                    <Text text={'Loading...'} fontWeight="semibold" size="lg" />
                </div>
            )}
            {!isAppOpenedInIframe() && (
                <form className={classes.form} onSubmit={(e) => e.preventDefault()}>
                    <Text text={loginTexts.SIGNIN_BTN_LABEL} variant="heading" fontWeight="semibold" size="lg" />
                    <div className={classes.inputContainer}>
                        <TextField
                            id="username"
                            required
                            value={reqObject.email}
                            label={loginTexts.USERNAME_LABEL}
                            error={errorObj.emailError}
                            helperText={errorObj.emailError ? errorObj.emailErrorText : undefined}
                            onChange={onChangeUsername}
                            name="email"
                            onKeyDown={onKeyDown}
                            placeholder="Username"
                        />
                        <PasswordField
                            id="password"
                            required
                            name="password"
                            label={loginTexts.PASSWORD_LABEL}
                            value={reqObject.password}
                            error={errorObj.passwordError}
                            helperText={errorObj.passwordError ? errorObj.passwordErrorText : undefined}
                            onChange={onChangePassword}
                            onKeyDown={onKeyDown}
                            placeholder="Password"
                        />
                        {loginErrorObj.loginError && <Text text={loginErrorObj.loginErrorMessage} size="xs" type="error" />}
                    </div>
                    <Button onClick={handleSubmit} variant="primary" size="lg" loading={isLoading}>
                        {loginTexts.SIGNIN_BTN_LABEL}
                    </Button>
                </form>
            )}
        </Card>
    ) : (
        <ErrorCard errorCode={errorCode} />
    );
};

export default LoginScreen;
